import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, Upload } from "lucide-react";
import { formatApiError } from "@/lib/api";
import PaymentIntegration from "@/pages/admin/PaymentIntegration";
import AdminCredentials from "@/pages/admin/AdminCredentials";

export default function AdminSettings() {
  const { setSettings } = useApp();
  const [s, setS] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const save = () => api.put("/admin/settings", s).then((r) => { setS(r.data); setSettings(r.data); toast.success("Settings saved"); });
  const uploadMusic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMusic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, music_url: data.url }));
      toast.success("Audio uploaded — click Save Settings to apply");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingMusic(false); e.target.value = ""; }
  };

  if (!s) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-6">Website Settings</h1>
      <div className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
        <div><Label className="text-sm">WhatsApp Number (with country code, no +)</Label><Input value={s.whatsapp_number || ""} onChange={set("whatsapp_number")} className="rounded-xl bg-sand" data-testid="settings-whatsapp" /></div>
        <div><Label className="text-sm">Hero Headline</Label><Input value={s.hero_headline || ""} onChange={set("hero_headline")} className="rounded-xl bg-sand" /></div>
        <div><Label className="text-sm">Hero Subtext</Label><Textarea value={s.hero_subtext || ""} onChange={set("hero_subtext")} className="rounded-xl bg-sand" rows={2} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-sm">Email</Label><Input value={s.email || ""} onChange={set("email")} className="rounded-xl bg-sand" /></div>
          <div><Label className="text-sm">Phone</Label><Input value={s.phone || ""} onChange={set("phone")} className="rounded-xl bg-sand" /></div>
          <div><Label className="text-sm">Instagram URL</Label><Input value={s.instagram || ""} onChange={set("instagram")} className="rounded-xl bg-sand" /></div>
          <div><Label className="text-sm">Facebook URL</Label><Input value={s.facebook || ""} onChange={set("facebook")} className="rounded-xl bg-sand" /></div>
        </div>
        <div><Label className="text-sm">Address</Label><Input value={s.address || ""} onChange={set("address")} className="rounded-xl bg-sand" /></div>
        <div><Label className="text-sm">WhatsApp Invoice Message — placeholders: {"{name} {order} {amount} {link}"}</Label><Textarea value={s.wa_invoice_template || ""} onChange={set("wa_invoice_template")} className="rounded-xl bg-sand" rows={6} data-testid="settings-wa-invoice" /></div>
        <div className="space-y-2">
          <Label className="text-sm">Background Music — plays softly across the site with a mute toggle. Upload a track or paste a URL; leave blank to use the default.</Label>
          <Input value={s.music_url || ""} onChange={set("music_url")} placeholder="/ambient-wellness.mp3 or https://.../track.mp3" className="rounded-xl bg-sand" data-testid="settings-music-url" />
          <div className="flex flex-wrap items-center gap-3">
            <input id="music-file" type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="settings-music-file" />
            <Button type="button" variant="outline" disabled={uploadingMusic} onClick={() => document.getElementById("music-file").click()} className="rounded-full border-forest text-forest" data-testid="settings-music-upload-btn">
              {uploadingMusic ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload audio
            </Button>
            {s.music_url && <audio src={s.music_url} controls className="h-9 max-w-full" data-testid="settings-music-preview" />}
          </div>
        </div>
        <Button onClick={save} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-settings-btn"><Save className="w-4 h-4 mr-1.5" /> Save Settings</Button>
      </div>

      <div className="mt-6"><AdminCredentials /></div>

      <div className="mt-6"><PaymentIntegration /></div>
    </div>
  );
}
